import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { X, MapPin, Star, Play, Share2 } from "lucide-react";
import { toast } from "sonner";
import { canNativeShare, nativeShare, copyText } from "@/lib/share";
import { trackEvent } from "@/lib/analytics";
import { fetchStoryCodes } from "@/pages/Story";
import MiniMap from "@/components/MiniMap";
import { useLang } from "@/context/LanguageContext";
import { useFavorites } from "@/lib/useFavorites";

export default function PanelHeader({ country, onClose }) {
  const { lang, t } = useLang();
  const { isFavorite, toggle } = useFavorites();
  const { data: storyCodes = [] } = useQuery({ queryKey: ["story-codes"], queryFn: fetchStoryCodes, staleTime: 600000 });
  const shareCountry = async () => {
    const name = country.names[lang] || country.names.en;
    const url = `${window.location.origin}/api/share/${country.code}?lang=${lang}`;
    const text = t("share_country_text").replace("{name}", name);
    trackEvent("share", { method: "panel", content_type: "country", item_id: country.code, lang });
    if (canNativeShare()) await nativeShare({ title: name, text, url });
    else { await copyText(`${text} ${url}`); toast.success(t("copied")); }
  };
  return (
    <>
      <div className="relative px-6 pt-4 pb-6 border-b border-stoneLine">
        <button
          onClick={onClose}
          className="absolute top-4 end-4 w-9 h-9 rounded-full border border-stoneLine bg-sand/60 hover:bg-sandDeep flex items-center justify-center transition-colors"
          aria-label={t("close")}
          data-testid="panel-close"
        >
          <X className="w-4 h-4" />
        </button>
        <button
          onClick={() => toggle(country.code)}
          className={`absolute top-4 end-14 w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
            isFavorite(country.code)
              ? "bg-ochre text-white border-ochre"
              : "border-stoneLine bg-sand/60 hover:bg-sandDeep text-ink"
          }`}
          aria-label={isFavorite(country.code) ? t("remove_favorite") : t("add_favorite")}
          aria-pressed={isFavorite(country.code)}
          data-testid="panel-favorite"
        >
          <Star className="w-4 h-4" fill={isFavorite(country.code) ? "currentColor" : "none"} />
        </button>

        <div className="flex items-start gap-4">
          <img
            key={country.flag_url}
            src={country.flag_url}
            alt={country.names[lang]}
            className="w-16 h-11 sm:w-24 sm:h-16 object-cover rounded-md shadow-sm border border-stoneLine animate-fade-in"
            data-testid="panel-flag"
          />
          <div
            key={`mm-${country.code}`}
            className="shrink-0 animate-fade-in w-12 h-12 sm:w-16 sm:h-16 [&>svg]:w-full [&>svg]:h-full"
          >
            <MiniMap code={country.code} flagUrl={country.flag_url} position={country.position} size={64} />
          </div>
          <div className="min-w-0 flex-1 pe-20">
            {country.disputed && (
              <span className="inline-block text-[10px] uppercase tracking-widest text-ochre border border-ochre/40 rounded-full px-2 py-0.5 mb-2">
                {t("disputed")}
              </span>
            )}
            <h2 className="text-xl sm:text-2xl font-display font-bold text-ink leading-tight" data-testid="panel-country-name">
              {country.names[lang]}
            </h2>
            <p className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> {country.capital[lang]}
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {storyCodes.includes(country.code) && (
            <Link to={`/story/${country.code}`} className="inline-flex items-center gap-2 rounded-full bg-ink text-sand pl-1.5 pr-4 py-1.5 text-sm font-semibold hover:bg-ochre transition-colors animate-fade-in" data-testid="story-watch-button">
              <span className="w-7 h-7 rounded-full bg-ochre flex items-center justify-center"><Play className="w-3.5 h-3.5 fill-current" /></span>
              {t("story_watch")}
            </Link>
          )}
          <button onClick={shareCountry} className="inline-flex items-center gap-1.5 rounded-full border border-stoneLine bg-card px-3.5 py-2 text-sm font-semibold text-ink hover:bg-ochre hover:text-white hover:border-ochre transition-colors" data-testid="panel-share-button">
            <Share2 className="w-4 h-4" /> {t("share")}
          </button>
        </div>
      </div>
    </>
  );
}
